import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Search } from "lucide-react";
import { api } from "../lib/api";
import { stagger } from "../lib/motion";
import Breadcrumb from "../components/site/Breadcrumb";
import ProductCard from "../components/site/ProductCard";

// Static category fallback so the left sidebar renders INSTANTLY on first paint,
// while the (identical) API response hydrates in the background.
const STATIC_CATEGORIES = [
  { slug: "paper-packaging", name: "Paper Packaging" },
  { slug: "sugarcane-bagasse", name: "Sugarcane / Bagasse" },
  { slug: "plastic-pp", name: "Plastic & PP Ware" },
  { slug: "aluminium-films", name: "Aluminium & Films" },
  { slug: "cutlery-accessories", name: "Cutlery & Accessories" },
];

// Module-level cache — first visit fetches, subsequent visits + category tab
// switches use the cached list and filter client-side (only ~20 products).
const cache = { products: null, categories: null };

function CardSkeleton() {
  return (
    <div className="bg-white border border-[#E2E8E4] rounded-2xl overflow-hidden">
      <div className="aspect-[4/3] bg-[#F0F4F0] animate-pulse" />
      <div className="p-5 space-y-3">
        <div className="h-4 w-3/4 bg-[#F0F4F0] rounded animate-pulse" />
        <div className="h-3 w-full bg-[#F0F4F0] rounded animate-pulse" />
        <div className="h-9 w-full bg-[#F0F4F0] rounded-full animate-pulse mt-2" />
      </div>
    </div>
  );
}

export default function Products() {
  const [params, setParams] = useSearchParams();
  const [allProducts, setAllProducts] = useState(cache.products);
  const [categories, setCategories] = useState(cache.categories || STATIC_CATEGORIES);
  const [search, setSearch] = useState("");
  const active = params.get("category") || "all";

  useEffect(() => {
    if (!cache.categories) {
      api.categories().then((c) => {
        cache.categories = c;
        setCategories(c);
      }).catch(() => {});
    }
    if (!cache.products) {
      api.products({}).then((p) => {
        cache.products = p;
        setAllProducts(p);
      }).catch(() => setAllProducts([]));
    }
  }, []);

  // Client-side filter + search — instant on tab switch / typing.
  const products = useMemo(() => {
    if (!allProducts) return null;
    const s = search.trim().toLowerCase();
    return allProducts.filter((p) => {
      const catOk = active === "all" || p.category_slug === active;
      const searchOk = !s || p.name.toLowerCase().includes(s) || p.description.toLowerCase().includes(s);
      return catOk && searchOk;
    });
  }, [allProducts, active, search]);

  const setCat = (slug) => {
    if (slug === "all") params.delete("category");
    else params.set("category", slug);
    setParams(params, { replace: true });
  };

  return (
    <div>
      <section className="bg-[#F8FAF8] border-b border-[#E2E8E4] pt-12 pb-14">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <Breadcrumb items={[{ label: "Products" }]} />
          <div className="mt-6 flex flex-wrap items-end justify-between gap-6">
            <div>
              <h1 className="font-head text-4xl md:text-6xl font-medium tracking-tighter text-[#0A1C0E]">
                Our Products
              </h1>
              <p className="text-lg text-[#3A4D3E] mt-3 max-w-xl">
                Food-grade packaging solutions across paper, sugarcane,
                plastic, aluminium & cutlery.
              </p>
            </div>
            <div className="relative w-full sm:w-80">
              <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#3A4D3E]" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search products..."
                data-testid="product-search"
                className="w-full bg-white border border-[#E2E8E4] rounded-full pl-11 pr-4 py-3 text-sm outline-none focus:border-[#1D6B2A] focus:ring-4 focus:ring-[#4CAF50]/15 transition-all"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-10">
          <aside className="lg:col-span-3">
            <div className="lg:sticky lg:top-28">
              <h3 className="text-xs font-bold tracking-[0.2em] uppercase text-[#1D6B2A] mb-4">
                Categories
              </h3>
              <div className="flex lg:flex-col gap-2 flex-wrap" data-testid="category-filter">
                <button
                  onClick={() => setCat("all")}
                  data-testid="category-all"
                  className={`text-left px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                    active === "all" ? "bg-[#1D6B2A] text-white" : "text-[#3A4D3E] hover:bg-[#F8FAF8]"
                  }`}
                >
                  All Products
                </button>
                {categories.map((c) => (
                  <button
                    key={c.slug}
                    onClick={() => setCat(c.slug)}
                    data-testid={`category-${c.slug}`}
                    className={`text-left px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                      active === c.slug ? "bg-[#1D6B2A] text-white" : "text-[#3A4D3E] hover:bg-[#F8FAF8]"
                    }`}
                  >
                    {c.name}
                  </button>
                ))}
              </div>
            </div>
          </aside>

          <div className="lg:col-span-9">
            {products === null ? (
              <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="products-loading">
                {Array.from({ length: 6 }).map((_, i) => (
                  <CardSkeleton key={i} />
                ))}
              </div>
            ) : products.length === 0 ? (
              <p className="text-[#3A4D3E] py-20 text-center">No products match your search.</p>
            ) : (
              <motion.div
                key={active + search}
                variants={stagger}
                initial="hidden"
                animate="show"
                className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6"
              >
                {products.map((p, i) => (
                  <ProductCard key={p.slug} product={p} index={i} priority={i < 6} />
                ))}
              </motion.div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
